import { useEffect, useRef } from 'react';
import { useCFAuth } from '@/lib/auth/cf-auth-context';
import { useNavigate } from '@tanstack/react-router';
import { Topbar } from '@/components/topbar';
import { TooltipProvider } from '@/components/ui/tooltip';
import { useSyncStore } from '@/lib/store/sync.store';
import { getStoredSessionId } from '@/lib/cloudflare/auth-client';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { EmailList } from '@/components/email-list';

// Kanban skeleton shown immediately while loading - matches actual kanban layout
function KanbanSkeleton() {
  const columns = [
    { name: 'Inbox', hasAction: false },
    { name: 'Unsubscribe', hasAction: true },
    { name: 'Archive', hasAction: true },
    { name: 'Delete', hasAction: false },
  ];

  return (
    <div className="h-[calc(100vh-3.5rem)] overflow-x-auto pt-1">
      <div className="inline-flex gap-3 px-2 min-w-full w-max h-full">
        {columns.map((col, i) => (
          <div key={col.name} className={cn("w-[300px] flex flex-col", i < 3 && "border-r border-border/10")}>
            <div className="px-2 py-0.5 flex items-center justify-between">
              <span className="text-[11px] text-muted-foreground">{col.name} <span className="text-[10px]">(0)</span></span>
              {col.hasAction && <Skeleton className="h-3 w-16" />}
            </div>
            <div className="flex-1 overflow-hidden p-1">
              {[1, 2, 3, 4, 5, 6, 7, 8].map((j) => (
                <div key={j} className="border-b border-border/40 px-2 py-1.5">
                  <div className="flex items-center justify-between mb-0.5">
                    <Skeleton className="h-3 w-24" />
                    <Skeleton className="h-2.5 w-12" />
                  </div>
                  <Skeleton className="h-3 w-full mb-0.5" />
                  <div className="flex items-center justify-between">
                    <Skeleton className="h-2.5 w-3/4" />
                    <Skeleton className="h-4 w-12 rounded" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AppContent() {
  const { user, status, accessToken } = useCFAuth();
  const navigate = useNavigate();
  const hasCheckedSync = useRef(false);
  const previousUserId = useRef<string | null>(null);
  const forceSyncOnTransition = useRef(false);

  const {
    hasCompletedInitialSync,
    currentJobId,
    progress,
    completeSync,
    reset: resetSync,
    startSync,
    setPolling,
    updateProgress,
    setHasCheckedSyncState,
  } = useSyncStore();

  useEffect(() => {
    const currentUserId = user?.id || null;
    if (previousUserId.current !== currentUserId) {
      if (previousUserId.current && previousUserId.current !== currentUserId) {
        forceSyncOnTransition.current = true;
      }
      previousUserId.current = currentUserId;
      hasCheckedSync.current = false;
    }
  }, [user]);

  // Background sync check - doesn't block rendering, shows kanban immediately
  // Note: isPolling and progress are now persisted, so Dynamic Island shows immediately on refresh
  useEffect(() => {
    if (status !== 'authenticated' || !user) return;
    if (hasCheckedSync.current) return;
    hasCheckedSync.current = true;
    setHasCheckedSyncState(false);

    // Fast path: if localStorage says sync is done and no active job, we're good
    if (hasCompletedInitialSync && !currentJobId && !forceSyncOnTransition.current) {
      setHasCheckedSyncState(true);
      return;
    }

    // Run checks in parallel for speed
    const runChecks = async () => {
      try {
      const CF_WORKER_URL = import.meta.env.VITE_CLOUDFLARE_WORKER_URL || '';
      const sessionId = getStoredSessionId();
      const headers: Record<string, string> = {};
      if (sessionId) headers['X-Session-Id'] = sessionId;

      // If there's a stored job ID, verify its status with the server
      // The UI already shows sync progress (restored from localStorage), we just need to verify
      if (currentJobId) {
        try {
          const response = await fetch(`${CF_WORKER_URL}/api/sync-jobs/${currentJobId}`, {
            credentials: 'include',
            headers,
          });

          if (response.ok) {
            const result = await response.json();
            if (result.success) {
              const jobStatus = result.data.status;

              if (jobStatus === 'processing' || jobStatus === 'pending') {
                console.log('Verified in-progress sync job:', currentJobId);
                // Update with fresh data from server, polling will continue automatically
                const total = result.data.total || 0;
                const processed = result.data.processed || 0;
                updateProgress({
                  jobId: currentJobId,
                  status: jobStatus,
                  phase: result.data.phase || 'queued',
                  total,
                  processed,
                  analyzed: result.data.analyzed || 0,
                  cached: result.data.cached || 0,
                  failed: result.data.failed || 0,
                  percentComplete: total > 0
                    ? Math.round((processed / total) * 100)
                    : 0,
                });
                // Ensure polling is enabled (it should be from localStorage, but be safe)
                setPolling(true);
                return;
              } else if (jobStatus === 'completed') {
                console.log('Sync job completed while away:', currentJobId);
                completeSync();
                return;
              } else {
                // Job ended (failed/cancelled) - reset sync state
                console.log('Sync job ended with status:', jobStatus);
                resetSync();
              }
            }
          }
        } catch (error) {
          // Don't reset on network error - keep showing UI and let polling retry
          console.warn('Failed to verify job status, will retry via polling:', error);
          // Ensure polling is enabled so it can recover
          setPolling(true);
          return;
        }
      }

      const isFreshData = (result?: { receivedAt?: number | string | null }): boolean => {
        const latestReceivedAt = Number(result?.receivedAt || 0);
        if (!Number.isFinite(latestReceivedAt) || latestReceivedAt <= 0) return false;
        const nowSeconds = Date.now() / 1000;
        const maxAgeSeconds = 24 * 60 * 60;
        return nowSeconds - latestReceivedAt <= maxAgeSeconds;
      };

      // Check D1 for existing emails (in case localStorage was cleared)
      if (!forceSyncOnTransition.current) {
        try {
          const response = await fetch(`${CF_WORKER_URL}/api/emails?userId=${user!.id}&limit=1`, {
            credentials: 'include',
            headers,
          });
          if (response.ok) {
            const result = await response.json();
            if (result.success && result.data?.emails && result.data.emails.length > 0) {
              const latestEmail = result.data.emails[0];
              if (isFreshData(latestEmail)) {
                console.log('Found existing analyzed emails in D1 and they are fresh, skipping sync');
                completeSync();
                return;
              }

              console.log('Found existing analyzed emails in D1 but they are stale, refreshing sync');
            }
          }
        } catch (error) {
          console.warn('Failed to check for existing emails:', error);
        }
      }

      // No existing fresh emails found - start sync automatically and show progress in dynamic island
      console.log('No emails found, starting initial sync');
      try {
        const syncResponse = await fetch(`${CF_WORKER_URL}/api/sync-jobs/start`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            ...headers,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            userId: user.id,
            userEmail: user.email,
            accessToken,
          }),
        });

        const syncResult = await syncResponse.json();
        if (syncResponse.ok && syncResult.success && syncResult.data?.jobId) {
          console.log('Started sync job:', syncResult.data.jobId);
          startSync(syncResult.data.jobId, syncResult.data.totalEmails || 0);
          setPolling(true);
        } else {
          console.error('Failed to start sync:', syncResult.error?.message || 'Unknown error');
        }
      } catch (error) {
        console.error('Failed to start sync:', error);
      }
      forceSyncOnTransition.current = false;
      } finally {
        setHasCheckedSyncState(true);
      }
    };

    runChecks();
  }, [status, user, accessToken, hasCompletedInitialSync, currentJobId, navigate, completeSync, resetSync, startSync, setPolling, setHasCheckedSyncState, updateProgress]);

  // Poll for sync progress when syncing
  useEffect(() => {
    if (!currentJobId || progress?.status !== 'processing') return;

    const CF_WORKER_URL = import.meta.env.VITE_CLOUDFLARE_WORKER_URL || '';
    const sessionId = getStoredSessionId();
    const headers: Record<string, string> = {};
    if (sessionId) headers['X-Session-Id'] = sessionId;

    const pollInterval = setInterval(async () => {
      try {
        const response = await fetch(`${CF_WORKER_URL}/api/sync-jobs/${currentJobId}`, {
          credentials: 'include',
          headers,
        });

        if (response.ok) {
          const result = await response.json();
          if (result.success) {
            const job = result.data;

            // Debug: log sync job response to see phase updates
            console.log('[sync-poll]', {
              phase: job.phase,
              status: job.status,
              processed: job.processed,
              total: job.total,
            });

            updateProgress({
              jobId: currentJobId,
              status: job.status,
              phase: job.phase || 'queued',
              total: job.total || 0,
              processed: job.processed || 0,
              analyzed: job.analyzed || 0,
              cached: job.cached || 0,
              failed: job.failed || 0,
              percentComplete: job.total > 0
                ? Math.round((job.processed / job.total) * 100)
                : 0,
            });

            if (job.status === 'completed') {
              console.log('Sync completed!');
              completeSync();
            } else if (job.status === 'failed') {
              console.error('Sync failed:', job.error_message);
              resetSync();
            }
          }
        }
      } catch (error) {
        console.warn('Failed to poll sync status:', error);
      }
    }, 2000);

    return () => clearInterval(pollInterval);
  }, [currentJobId, progress?.status, updateProgress, completeSync, resetSync]);

  // Show skeleton immediately - even while auth is loading
  // This gives users instant visual feedback
  return (
    <div className="flex flex-col h-screen">
      <Topbar />
      <main className="flex-grow w-full pt-11 overflow-hidden">
        <TooltipProvider>
          {status === 'loading' ? (
            <KanbanSkeleton />
          ) : status === 'unauthenticated' ? null : (
            <EmailList key={user?.id ?? 'unauthenticated'} />
          )}
        </TooltipProvider>
      </main>
    </div>
  );
}

export function AuthenticatedApp() {
  return <AppContent />;
}
